// Utility functions to convert between WYSIWYG rule format and backend JSON format

export const conditionsToJson = (conditions, ruleType = 'fulfillment') => {
  if (!conditions || conditions.length === 0) {
    return {};
  }

  // For import rules, use the backend's expected schema
  if (ruleType === 'import') {
    return buildImportRuleConditions(conditions);
  }

  // For fulfillment rules, use the original MongoDB-style format
  return buildFulfillmentRuleConditions(conditions);
};

const buildImportRuleConditions = (conditions) => {
  const result = {};
  
  conditions.forEach(condition => {
    const { field, operator, value } = condition;
    
    if (!field || !operator || value === '') {
      return;
    }

    switch (field) {
      case 'tags':
        if (operator === 'in' || operator === 'not_in') {
          result.tags = value.split(',').map(v => v.trim());
          result.tag_match_type = operator === 'in' ? 'any' : 'all'; // For not_in, we'll handle this differently
        } else if (operator === 'equals' || operator === 'contains' || operator === 'starts_with' || operator === 'ends_with') {
          result.tags = [value];
          result.tag_match_type = 'any';
        }
        break;
      case 'skus':
        if (operator === 'in' || operator === 'not_in') {
          result.skus = value.split(',').map(v => v.trim());
          result.sku_match_type = operator === 'in' ? 'any' : 'all';
        } else if (operator === 'equals') {
          result.skus = [value];
          result.sku_match_type = 'any';
        }
        break;
      case 'financial_status':
        if (operator === 'in' || operator === 'equals') {
          result.financial_status = operator === 'equals' ? [value] : value.split(',').map(v => v.trim());
        }
        break;
      case 'status':
      case 'fulfillment_status': // Backward compatibility
      case 'order_status': // Backward compatibility
        if (operator === 'in' || operator === 'equals') {
          result.status = operator === 'equals' ? [value] : value.split(',').map(v => v.trim());
        }
        break;
      case 'payment_gateways':
        if (operator === 'in' || operator === 'equals') {
          result.payment_gateways = operator === 'equals' ? [value] : value.split(',').map(v => v.trim());
        }
        break;
      case 'min_order_value':
        if (operator === 'greater_than_or_equal' || operator === 'greater_than') {
          result.min_order_value = parseFloat(value);
        }
        break;
      case 'max_order_value':
        if (operator === 'less_than_or_equal' || operator === 'less_than') {
          result.max_order_value = parseFloat(value);
        }
        break;
      default:
        // For other fields, we might need to handle them as string patterns
        if (field.includes('email')) {
          result.customer_email_patterns = result.customer_email_patterns || [];
          if (operator === 'contains') {
            result.customer_email_patterns.push(`*${value}*`);
          } else if (operator === 'starts_with') {
            result.customer_email_patterns.push(`${value}*`);
          } else if (operator === 'ends_with') {
            result.customer_email_patterns.push(`*${value}`);
          } else if (operator === 'equals') {
            result.customer_email_patterns.push(value);
          }
        }
        break;
    }
  });

  return result;
};

const buildFulfillmentRuleConditions = (conditions) => {
  // Simple case: single condition
  if (conditions.length === 1) {
    const condition = conditions[0];
    return buildConditionObject(condition);
  }

  // Multiple conditions: need to handle AND/OR logic
  const result = {};
  let currentGroup = [];
  let currentConnector = 'and';

  conditions.forEach((condition, index) => {
    if (index === 0) {
      currentGroup.push(buildConditionObject(condition));
      return;
    }

    if (condition.connector === currentConnector) {
      // Same connector, add to current group
      currentGroup.push(buildConditionObject(condition));
    } else {
      // Different connector, finalize current group and start new one
      if (currentGroup.length > 1) {
        result[currentConnector] = currentGroup;
      } else {
        Object.assign(result, currentGroup[0]);
      }
      
      currentGroup = [buildConditionObject(condition)];
      currentConnector = condition.connector;
    }
  });

  // Handle the last group
  if (currentGroup.length > 1) {
    if (Object.keys(result).length > 0) {
      // We already have conditions, need to wrap everything
      return { and: [result, { [currentConnector]: currentGroup }] };
    } else {
      result[currentConnector] = currentGroup;
    }
  } else {
    Object.assign(result, currentGroup[0]);
  }

  return result;
};

export const jsonToConditions = (json, ruleType = 'fulfillment') => {
  if (!json || Object.keys(json).length === 0) {
    return [{ field: '', operator: '', value: '', connector: 'and' }];
  }

  const conditions = [];
  
  if (ruleType === 'import') {
    parseImportRuleJson(json, conditions);
  } else {
    parseJsonObject(json, conditions);
  }
  
  // Ensure we have at least one condition
  if (conditions.length === 0) {
    return [{ field: '', operator: '', value: '', connector: 'and' }];
  }

  return conditions;
};

const parseImportRuleJson = (json, conditions) => {
  // Handle tags
  if (json.tags && Array.isArray(json.tags)) {
    conditions.push({
      field: 'tags',
      operator: json.tags.length > 1 ? 'in' : 'equals',
      value: json.tags.length > 1 ? json.tags.join(', ') : json.tags[0],
      connector: 'and'
    });
  }

  // Handle SKUs
  if (json.skus && Array.isArray(json.skus)) {
    conditions.push({
      field: 'skus',
      operator: json.skus.length > 1 ? 'in' : 'equals',
      value: json.skus.length > 1 ? json.skus.join(', ') : json.skus[0],
      connector: 'and'
    });
  }

  // Handle status fields
  ['financial_status', 'status'].forEach(field => {
    if (json[field] && Array.isArray(json[field])) {
      conditions.push({
        field,
        operator: json[field].length > 1 ? 'in' : 'equals',
        value: json[field].length > 1 ? json[field].join(', ') : json[field][0],
        connector: 'and'
      });
    }
  });

  // Handle payment gateways
  if (json.payment_gateways && Array.isArray(json.payment_gateways)) {
    conditions.push({
      field: 'payment_gateways',
      operator: json.payment_gateways.length > 1 ? 'in' : 'equals',
      value: json.payment_gateways.length > 1 ? json.payment_gateways.join(', ') : json.payment_gateways[0],
      connector: 'and'
    });
  }

  // Handle min/max order values
  if (json.min_order_value !== undefined) {
    conditions.push({
      field: 'min_order_value',
      operator: 'greater_than_or_equal',
      value: json.min_order_value.toString(),
      connector: 'and'
    });
  }

  if (json.max_order_value !== undefined) {
    conditions.push({
      field: 'max_order_value',
      operator: 'less_than_or_equal',
      value: json.max_order_value.toString(),
      connector: 'and'
    });
  }

  // Handle customer email patterns
  if (json.customer_email_patterns && Array.isArray(json.customer_email_patterns)) {
    json.customer_email_patterns.forEach(pattern => {
      let operator = 'equals';
      let value = pattern;

      if (pattern.startsWith('*') && pattern.endsWith('*')) {
        operator = 'contains';
        value = pattern.slice(1, -1);
      } else if (pattern.startsWith('*')) {
        operator = 'ends_with';
        value = pattern.slice(1);
      } else if (pattern.endsWith('*')) {
        operator = 'starts_with';
        value = pattern.slice(0, -1);
      }

      conditions.push({
        field: 'customer_email',
        operator,
        value,
        connector: 'and'
      });
    });
  }
};

const buildConditionObject = (condition) => {
  const { field, operator, value } = condition;
  
  if (!field || !operator || value === '') {
    return {};
  }

  const conditionObj = {};
  
  switch (operator) {
    case 'equals':
      conditionObj[field] = value;
      break;
    case 'not_equals':
      conditionObj[field] = { $ne: value };
      break;
    case 'contains':
      conditionObj[field] = { $regex: value, $options: 'i' };
      break;
    case 'not_contains':
      conditionObj[field] = { $not: { $regex: value, $options: 'i' } };
      break;
    case 'starts_with':
      conditionObj[field] = { $regex: `^${value}`, $options: 'i' };
      break;
    case 'ends_with':
      conditionObj[field] = { $regex: `${value}$`, $options: 'i' };
      break;
    case 'greater_than':
      conditionObj[field] = { $gt: parseFloat(value) };
      break;
    case 'less_than':
      conditionObj[field] = { $lt: parseFloat(value) };
      break;
    case 'greater_than_or_equal':
      conditionObj[field] = { $gte: parseFloat(value) };
      break;
    case 'less_than_or_equal':
      conditionObj[field] = { $lte: parseFloat(value) };
      break;
    case 'in':
      conditionObj[field] = { $in: value.split(',').map(v => v.trim()) };
      break;
    case 'not_in':
      conditionObj[field] = { $nin: value.split(',').map(v => v.trim()) };
      break;
    default:
      conditionObj[field] = value;
  }

  return conditionObj;
};

const parseJsonObject = (obj, conditions, connector = 'and') => {
  if (!obj || typeof obj !== 'object') return;

  Object.entries(obj).forEach(([key, value]) => {
    if (key === 'and' || key === 'or') {
      // Logical operator
      if (Array.isArray(value)) {
        value.forEach((item, index) => {
          parseJsonObject(item, conditions, key);
        });
      }
    } else {
      // Field condition
      const condition = parseFieldCondition(key, value);
      if (condition) {
        condition.connector = connector;
        conditions.push(condition);
      }
    }
  });
};

const parseFieldCondition = (field, value) => {
  if (typeof value === 'string' || typeof value === 'number') {
    return {
      field,
      operator: 'equals',
      value: value.toString(),
      connector: 'and'
    };
  }

  if (typeof value === 'object' && value !== null) {
    // Handle complex operators
    const operators = Object.keys(value);
    if (operators.length === 1) {
      const op = operators[0];
      const val = value[op];

      switch (op) {
        case '$ne':
          return { field, operator: 'not_equals', value: val.toString(), connector: 'and' };
        case '$gt':
          return { field, operator: 'greater_than', value: val.toString(), connector: 'and' };
        case '$lt':
          return { field, operator: 'less_than', value: val.toString(), connector: 'and' };
        case '$gte':
          return { field, operator: 'greater_than_or_equal', value: val.toString(), connector: 'and' };
        case '$lte':
          return { field, operator: 'less_than_or_equal', value: val.toString(), connector: 'and' };
        case '$in':
          return { field, operator: 'in', value: Array.isArray(val) ? val.join(', ') : val.toString(), connector: 'and' };
        case '$nin':
          return { field, operator: 'not_in', value: Array.isArray(val) ? val.join(', ') : val.toString(), connector: 'and' };
        case '$regex':
          // Determine the type of regex
          const pattern = val;
          
          if (pattern.startsWith('^')) {
            return { field, operator: 'starts_with', value: pattern.substring(1), connector: 'and' };
          } else if (pattern.endsWith('$')) {
            return { field, operator: 'ends_with', value: pattern.slice(0, -1), connector: 'and' };
          } else {
            return { field, operator: 'contains', value: pattern, connector: 'and' };
          }
        case '$not':
          if (val.$regex) {
            return { field, operator: 'not_contains', value: val.$regex, connector: 'and' };
          }
          break;
        default:
          return { field, operator: 'equals', value: val.toString(), connector: 'and' };
      }
    }
  }

  return null;
}; 